import { isAbortError } from 'util/is-abort-error';
import { isFirebaseAuthUrl } from 'services/couch-login-firebase-ready';

/**
 * What the global `window.fetch` wrapper (`MyApp.installHttpErrorHandler`) does with a fetch that
 * rejected. The wrapper rethrows the error to its caller in every case; this only decides what it
 * reports or logs. For most URLs that happens before the rethrow; for Firebase Auth's own requests
 * it happens after it, in the background (#6174). See `design-docs/telemetry.md`, "Global Fetch Wrapper".
 */
export enum FetchFailureAction {
  /** the app cancelled the request itself: rethrow with no log, no report and no online check */
  PassThrough = 'PassThrough',
  /** the device really is offline: log only */
  LogOffline = 'LogOffline',
  /** the caller marked the request as one whose failures it retries and reports itself: log only */
  LogCallerOwned = 'LogCallerOwned',
  /** online, but the URL belongs to a service with its own retry/error handling: log only */
  LogThirdParty = 'LogThirdParty',
  /** online and first-party: report to Sentry */
  Report = 'Report',
}

// if url points to a third-party service or one with its own retry/error handling, don't report to Sentry
export function isThirdPartyOrSelfHealingUrl(url: string): boolean {
  return url.includes('/api/t/')                          // Mixpanel tracking proxy
    || url.includes('/api/flushTelemetry')                // telemetry has its own exponential backoff + server-side queue
    || url.includes('.db.mh-physique.app')                // CouchDB (has own sync/retry)
    || url.includes('securetoken.googleapis.com')         // Firebase Auth (has own retry)
    || url.includes('identitytoolkit.googleapis.com')     // Firebase Auth
    || url.includes('refiner.io')                         // Refiner survey SDK
    || url.includes('sentry.io')                          // Sentry's own reporting
    || url.includes('apis.google.com')                    // Google Auth
    // Paddle.js price preview: paddle-price-preview-failure.ts reports its network failures as info itself
    || url.includes('//api.paddle.com/')
    || url.includes('//sandbox-api.paddle.com/');
}

// the request options of every fetch whose caller retries and reports its own failures (#6058)
const callerOwnedRequests = new WeakSet<object>();

/**
 * Mark a fetch's options as belonging to a caller that retries and reports its own failures, so
 * the global wrapper only logs a failure of that request instead of filing a second report.
 * Returns the same object, to be passed straight to `fetch`. The mark lives in the app only and
 * is never sent to the server.
 */
export function markCallerReportsFailure<T extends object>(init: T): T {
  callerOwnedRequests.add(init);
  return init;
}

/** Whether the fetch with these options was marked by `markCallerReportsFailure`. */
export function callerReportsFailure(init: unknown): boolean {
  return typeof init === 'object' && init !== null && callerOwnedRequests.has(init);
}

/**
 * Decide what to do with a rejected fetch.
 *
 * `init` is the options object the caller passed to `fetch` (its second argument), if any.
 *
 * `isOfflineAsync` asks whether the device is really offline. It is a network request of its own,
 * so it is only called when the answer matters.
 */
export async function classifyFetchRejectionAsync(
  error: unknown,
  url: string,
  init: unknown,
  isOfflineAsync: () => Promise<boolean>,
): Promise<FetchFailureAction> {
  // A request the app itself called off is not a failure: `AbortController.abort()` makes
  // the pending fetch reject with an AbortError, and the caller asked for exactly that.
  // Reporting it filed a Sentry problem on every keystroke of the food search, which drops
  // its in-flight request whenever the text changes (#5410). Checked before the online
  // check below on purpose -- that check is itself a network request, and a cancellation is
  // no reason to go and ask whether the device is online.
  //
  // Only a deliberate abort is let through here. A deadline set with `AbortSignal.timeout`
  // rejects with a TimeoutError (DOMException code 23) instead, and a timeout IS worth
  // reporting, so it still is.
  if (isAbortError(error)) {
    return FetchFailureAction.PassThrough;
  }

  // if the caller retries and reports this request's failures itself, a report here would be a
  // second one for the same failure -- filed even when the caller's retry then succeeds (#6058).
  // Checked before the online check, which would only be an extra network request.
  if (callerReportsFailure(init)) {
    return FetchFailureAction.LogCallerOwned;
  }

  // if actually offline, just log
  if (await isOfflineAsync()) {
    return FetchFailureAction.LogOffline;
  }

  // if third-party/self-healing, its owner has its own retry logic, so don't report to Sentry
  if (isThirdPartyOrSelfHealingUrl(url)) {
    return FetchFailureAction.LogThirdParty;
  }

  // ping passed but fetch failed on a first-party URL: report
  return FetchFailureAction.Report;
}

/** What `handleFetchRejectionAsync` needs from the app. Injected so tests can drive it by hand. */
export interface FetchRejectionHandlers {
  /** Asks whether the device is really offline. In the app it may also change the connection state. */
  isOfflineAsync: () => Promise<boolean>;
  /** Logs or reports the failure as the classifier decided. */
  act: (action: FetchFailureAction, url: string, error: unknown) => void;
  /** Reports a failure of the classification that ran after the error was already handed back. */
  reportBackgroundFailure: (url: string, err: unknown) => void;
  /** Called at once for a failed Firebase Auth request, so a sign-in timeout report can name it. */
  noteFirebaseAuthFailure: (url: string, error: unknown) => void;
}

/**
 * What the global fetch wrapper does with a fetch that rejected: decide what to log or report,
 * and rethrow the original error to the caller.
 *
 * #6174: a failed Firebase Auth request is rethrown at once and classified in the background.
 * Waiting for the online check deadlocked: the check switches the app to Online, the switch
 * waits for Firebase to finish signing in (`firebaseReadyAsync('online-flip')`), and Firebase
 * waits for this very error. Every other URL still waits, because its callers rely on the
 * connection state being up to date when they see the error. design-docs/telemetry.md,
 * "Global Fetch Wrapper".
 */
export async function handleFetchRejectionAsync(error: unknown, url: string, init: unknown, handlers: FetchRejectionHandlers): Promise<never> {
  const classifyAndActAsync = async () => {
    const action = await classifyFetchRejectionAsync(error, url, init, handlers.isOfflineAsync);
    handlers.act(action, url, error);
  };
  // if Firebase Auth's own request failed, hand the error straight back and classify it in the background
  if (isFirebaseAuthUrl(url) && !isAbortError(error)) {
    handlers.noteFirebaseAuthFailure(url, error);
    void classifyAndActAsync().catch(err => handlers.reportBackgroundFailure(url, err));
    throw error;
  }
  await classifyAndActAsync();
  throw error;
}
